import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import {
  MeasurementTypes,
  SelectMeasurementPayloadType,
} from "../../../../../../@types/Measurements";
import { Tag } from "../../../../../../components/Tag";
import { getSpirometryGradeTagVariant } from "../../../../../../constants/spirometryConstants";
import { useArtiQSpirometryData } from "../../../../../../hooks/queries/spirometry";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import { useToast } from "../../../../../../hooks/useToast";
import { reportService } from "../../../../../../services/reportService";
import { isProduction, isUat } from "../../../../../../utils/environment";
import { mixpanelActions } from "../../../../../../utils/mixpanel";
import {
  HeaderContainer,
  Title,
  Subtitle,
} from "../../MeasurementModal.styles";
import { BlowAcceptabilitySection } from "../BlowAcceptabilitySection";
import { OptionsPopover } from "../OptionsPopover";
import { SpirometryValuesSection } from "../SpirometryValuesSection";
import { DownloadingOverlay } from "./DownloadingOverlay";
import { SessionDetailsEmptyState } from "./SessionDetailsEmptyState";
import {
  ArtiQContainer,
  ChartContainer,
  ContentContainer,
  SpirometryContainer,
  SubtitleGradeContainer,
} from "./SpirometrySection.styles";
import { SpirometrySectionChart } from "./SpirometrySectionChart";

type SpirometrySectionProps = {
  selectedMeasurement: SelectMeasurementPayloadType | null;
  isPinnedGraph?: boolean;
  measurementType: MeasurementTypes;
  unit?: string;
  patientId?: string;
  isPinned?: boolean;
  onPinnedChange?: (isPinned: boolean) => void;
};

export function SpirometrySection({
  selectedMeasurement,
  isPinnedGraph,
  unit,
  patientId,
  measurementType,
  isPinned,
  onPinnedChange,
}: SpirometrySectionProps) {
  const { t } = useTranslation();

  const { widthView } = useIsMobile();
  const { showToast } = useToast();

  const [isExporting, setIsExporting] = useState(false);

  const hasSelectedMeasurement =
    selectedMeasurement?.spirometryTestIds &&
    selectedMeasurement.spirometryTestIds.length > 0;

  const { artiQData, isFetching, refetch, isRefetching } =
    useArtiQSpirometryData(
      hasSelectedMeasurement ? selectedMeasurement.spirometryTestIds! : []
    );

  const [linesToShow, setLinesToShow] = useState([
    true,
    true,
    true,
    true,
    true,
    true,
    true,
    true,
  ]);
  const [selectedBlow, setSelectedBlow] = useState<number>();
  const [hoveredLine, setHoveredLine] = useState<number>();
  const [archivedSpirometryTestsIds, setArchivedSpirometryTestsIds] = useState<
    number[]
  >([]);
  const [graphSectionKey, setGraphSectionKey] = useState(uuidv4());

  const fvcGrade = artiQData?.fvcGrade;
  const fev1Grade = artiQData?.fev1Grade;

  const fvcGradeVariant = getSpirometryGradeTagVariant(fvcGrade);
  const fev1GradeVariant = getSpirometryGradeTagVariant(fev1Grade);

  // EN-5002: session grades are not ready for production/UAT rollout — only
  // show them on dev/staging until product green-lights them.
  const showSessionGrades = !isProduction() && !isUat();

  useEffect(() => {
    mixpanelActions.track("User Action: Clicked Spirometry Datapoint");
  }, [selectedMeasurement]);

  // EN-4852: export the selected spirometry session as a PDF. Mirrors the blob
  // download idiom used for the discharge summary in PatientList.tsx.
  const handleExportSessionPdf = async () => {
    // Every blow in a session shares the same session_id; the export endpoint
    // is keyed on it. The first blow can occasionally be missing it (e.g.
    // archived/older data), so pick the first blow that actually carries one.
    const sessionId = artiQData?.spirometryTests?.find(
      (st: any) => st?.session_id
    )?.session_id;

    // Only the session_id is required to export — patientId is used purely for
    // the filename fallback. Never fail silently: without a session_id there is
    // nothing to export, so tell the user instead of no-op'ing on click.
    if (!sessionId) {
      showToast(t("patientPage_measurementModal_exportFailed"), "error");
      return;
    }

    mixpanelActions.track("User Action: Export Spirometry Session PDF");
    setIsExporting(true);

    try {
      const result = await reportService.downloadSpirometrySessionReport(
        sessionId
      );

      if (result.status < 200 || result.status >= 300) {
        showToast(t("patientPage_measurementModal_exportFailed"), "error");
        return;
      }

      const contentDisposition = result.headers["content-disposition"];
      const fileName =
        contentDisposition?.match(/filename\*=UTF-8''(.+)/)?.[1] ||
        contentDisposition?.match(/filename=(.+);?/)?.[1] ||
        `spirometry-session-${patientId ?? sessionId}.pdf`;

      const url = URL.createObjectURL(new Blob([result.data]));
      const link = Object.assign(document.createElement("a"), {
        href: url,
        download: fileName,
      });
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast(t("patientPage_measurementModal_exportFailed"), "error");
    } finally {
      setIsExporting(false);
    }
  };

  const volumeFlowValues = (artiQData?.flowVolumeAxes ?? []).map(
    (axis: any, index: number) => {
      const spirometryTest = artiQData?.spirometryTests?.[index];

      const isLineArchived = spirometryTest
        ? archivedSpirometryTestsIds.includes(spirometryTest.spirometry_test_id)
        : false;

      return {
        data: axis,
        isLineArchived,
      };
    }
  );

  const timeVolumeValues = (artiQData?.timeVolumeAxes ?? []).map(
    (axis: any, index: number) => {
      const spirometryTest = artiQData?.spirometryTests?.[index];

      const isLineArchived = spirometryTest
        ? archivedSpirometryTestsIds.includes(spirometryTest.spirometry_test_id)
        : false;

      return {
        data: axis,
        isLineArchived,
      };
    }
  );

  const measurementChartWidth = useMemo(() => {
    return widthView > 1530 ? 252 : 252;
  }, [widthView]);

  const yLabelConfig = (value: string) => ({
    value,
    angle: -90,
    fontSize: "12px",
    position: "insideLeft",
  });

  const xLabelConfig = (value: string) => ({
    value,
    fontSize: "12px",
    position: "insideBottomLeft",
  });

  const handleSelectedMeasurement = () => {
    const id = selectedMeasurement?.spirometryTestIds
      ? selectedMeasurement.spirometryTestIds[0]
      : undefined;

    const selectedIndex = artiQData?.spirometryTests?.findIndex(
      (st: any) => st.spirometry_test_id === id
    );

    if (selectedIndex >= 0) {
      setSelectedBlow(selectedIndex);
    }
  };

  useEffect(() => {
    setLinesToShow([true, true, true, true, true, true, true, true]);

    handleSelectedMeasurement();

    refetch();

    const archivedSpirometryTests: number[] = [];

    artiQData?.spirometryTests?.forEach((st: any) => {
      if (st?.archived_by !== undefined && st?.archived_by !== "") {
        archivedSpirometryTests.push(st.spirometry_test_id);
      }
    });

    setArchivedSpirometryTestsIds(archivedSpirometryTests);
  }, [selectedMeasurement, artiQData]);

  if (!hasSelectedMeasurement) {
    return (
      <SessionDetailsEmptyState
        isPinnedGraph={isPinnedGraph}
        measurementChartWidth={measurementChartWidth}
        isPinned={isPinned}
        onPinnedChange={onPinnedChange}
      />
    );
  }

  return (
    <SpirometryContainer isPinnedGraph={isPinnedGraph}>
      {isExporting && <DownloadingOverlay />}

      <OptionsPopover
        onExport={handleExportSessionPdf}
        isExporting={isExporting}
        disabled={!hasSelectedMeasurement || isFetching}
        isPinned={isPinned}
        onPinnedChange={onPinnedChange}
      />

      <HeaderContainer css={{ marginLeft: 0, marginBottom: 38 }}>
        <Title>
          {t("patientPage_measurementModal_spirometry_selectedSession")}
        </Title>

        <SubtitleGradeContainer>
          <Subtitle css={{ marginTop: 0, marginBottom: 0 }}>
            {selectedMeasurement.time}
          </Subtitle>

          {showSessionGrades && fvcGrade && fvcGradeVariant && (
            <Tag variant={fvcGradeVariant}>FVC = {fvcGrade}</Tag>
          )}

          {showSessionGrades && fev1Grade && fev1GradeVariant && (
            <Tag variant={fev1GradeVariant}>FEV1 = {fev1Grade}</Tag>
          )}

          {artiQData?.percentPredictedWarning &&
          !isFetching &&
          !isRefetching ? (
            <Tag variant="error">{artiQData.percentPredictedWarning}</Tag>
          ) : null}
        </SubtitleGradeContainer>
      </HeaderContainer>

      <ContentContainer isPinnedGraph={isPinnedGraph}>
        <ChartContainer
          css={{ width: measurementChartWidth }}
          key={graphSectionKey}
        >
          <SpirometrySectionChart
            xConfig={{
              dataKey: "x",
              type: "number",
              minTickGap: 2,
              allowDecimals: false,
              label: xLabelConfig("Vol (L)"),
              domain: [0, 8],
              tickFormatter(value) {
                return Number.parseFloat(value).toFixed(1);
              },
            }}
            yConfig={{
              axisSize: 42,
              label: yLabelConfig("Flow (L/s)"),
              domain: [0, 12],
            }}
            chartConfig={[
              {
                yKey: "y",
                dot: false,
                color: "#7562A8",
                lineType: "monotone",
              },
            ]}
            width="100%"
            height={207}
            data={volumeFlowValues ?? []}
            linesToShow={linesToShow}
            selectedBlow={selectedBlow}
            hoveredBlow={hoveredLine}
            onMouseEnter={(index) => {
              setHoveredLine(index);
              setGraphSectionKey(uuidv4());
            }}
            onMouseLeave={() => {
              setHoveredLine(undefined);
              setGraphSectionKey(uuidv4());
            }}
          />

          <SpirometrySectionChart
            xConfig={{
              dataKey: "x",
              type: "number",
              minTickGap: 2,
              allowDecimals: false,
              label: xLabelConfig("Time (s)"),
              domain: [0, 8],
              tickFormatter(value) {
                return Number.parseFloat(value).toFixed(1);
              },
            }}
            yConfig={{
              axisSize: 42,
              label: yLabelConfig("Vol (L)"),
              domain: [0, 12],
            }}
            chartConfig={[
              {
                yKey: "y",
                dot: false,
                color: "#7562A8",
                lineType: "monotone",
              },
            ]}
            width="100%"
            height={207}
            data={timeVolumeValues ?? []}
            linesToShow={linesToShow}
            selectedBlow={selectedBlow}
            hoveredBlow={hoveredLine}
            onMouseEnter={(index) => {
              setHoveredLine(index);
              setGraphSectionKey(uuidv4());
            }}
            onMouseLeave={() => {
              setHoveredLine(undefined);
              setGraphSectionKey(uuidv4());
            }}
          />
        </ChartContainer>

        <ArtiQContainer>
          <SpirometryValuesSection
            spirometryTests={artiQData?.spirometryTests}
            archivedSpirometryTestsIds={archivedSpirometryTestsIds}
          />
          <BlowAcceptabilitySection
            patientId={patientId}
            unit={unit}
            measurementType={measurementType}
            archivedSpirometryTestsIds={archivedSpirometryTestsIds}
            blows={artiQData?.blowAcceptability}
            spirometryData={artiQData?.spirometryTests}
            hoveredBlowIndex={hoveredLine}
            selectedBlowIndex={selectedBlow}
            onRowHover={(index) => {
              setHoveredLine(index);
              setGraphSectionKey(uuidv4());
            }}
            refetch={refetch}
          />
        </ArtiQContainer>
      </ContentContainer>
    </SpirometryContainer>
  );
}
